import { CoursePhase } from '@/models/course';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { addButtonStyle, dangerButtonStyle, iconButtonStyle } from '@/shared/dark-inline-styles';
const uuidv4 = () => crypto.randomUUID();
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import PlusIcon from 'mdi-react/PlusIcon';

interface PhasesEditorProps {
    phases: CoursePhase[];
    onChange: (phases: CoursePhase[]) => void;
}

const PhasesEditor = ({ phases, onChange }: PhasesEditorProps) => {
    const updatePhase = (index: number, patch: Partial<CoursePhase>) => {
        const next = phases.map((p, i) => (i === index ? { ...p, ...patch } : p));
        onChange(next);
    };

    const addPhase = () => {
        onChange([
            ...phases,
            { id: uuidv4(), order: phases.length, title: '', subtitle: '', description: '', daysLabel: '', color: '' },
        ]);
    };

    const removePhase = (index: number) => {
        onChange(phases.filter((_, i) => i !== index).map((p, i) => ({ ...p, order: i })));
    };

    const movePhase = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= phases.length) return;
        const next = [...phases];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next.map((p, i) => ({ ...p, order: i })));
    };

    return (
        <div>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Phases (optional — used for grouped courses like Mind Mastery)</p>
            {phases.length === 0 && <p style={{ color: '#999999', fontSize: 13 }}>No phases — lessons render as a flat list.</p>}
            {phases.map((phase, idx) => (
                <div
                    key={phase.id}
                    style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 10,
                        background: 'rgba(0,0,0,0.02)',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong>Phase {idx + 1}</strong>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" onClick={() => movePhase(idx, -1)} disabled={idx === 0} title="Move up" style={iconButtonStyle}>
                                <ArrowUpIcon size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => movePhase(idx, 1)}
                                disabled={idx === phases.length - 1}
                                title="Move down"
                                style={iconButtonStyle}
                            >
                                <ArrowDownIcon size={16} />
                            </button>
                            <button type="button" onClick={() => removePhase(idx)} title="Delete phase" style={dangerButtonStyle}>
                                <DeleteIcon size={16} />
                            </button>
                        </div>
                    </div>
                    <MaterialTextField label="Title" value={phase.title} onChange={(e) => updatePhase(idx, { title: e.target.value })} />
                    <MaterialTextField
                        label="Subtitle"
                        value={phase.subtitle ?? ''}
                        onChange={(e) => updatePhase(idx, { subtitle: e.target.value })}
                    />
                    <MaterialTextField
                        label="Days Label (e.g. Days 1-7)"
                        value={phase.daysLabel ?? ''}
                        onChange={(e) => updatePhase(idx, { daysLabel: e.target.value })}
                    />
                    {/* Hex text + native colour picker stay in sync. The picker guarantees a
                        valid #RRGGBB; the text field allows clearing it (optional field). */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <MaterialTextField
                                label="Accent Color (hex)"
                                value={phase.color ?? ''}
                                onChange={(e) => updatePhase(idx, { color: e.target.value })}
                            />
                        </div>
                        <input
                            type="color"
                            aria-label={`Phase ${idx + 1} accent colour picker`}
                            value={/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(phase.color ?? '') ? (phase.color as string) : '#000000'}
                            onChange={(e) => updatePhase(idx, { color: e.target.value })}
                            style={{ width: 40, height: 40, padding: 0, border: '1px solid #e0e0e0', borderRadius: 6, cursor: 'pointer' }}
                        />
                    </div>
                    <MaterialTextField
                        label="Description"
                        multiline
                        value={phase.description ?? ''}
                        onChange={(e) => updatePhase(idx, { description: e.target.value })}
                    />
                </div>
            ))}
            <button type="button" onClick={addPhase} style={addButtonStyle}>
                <PlusIcon size={16} /> Add Phase
            </button>
        </div>
    );
};

export default PhasesEditor;
